import { useEffect, useState, lazy, Suspense } from 'react'
import { ArrowDownIcon, ArrowUpIcon } from '@chakra-ui/icons'
import { chakra, Button, Center, HStack, Image, Spinner } from '@chakra-ui/react'
import useBreakPoint from 'hooks/useBreakPoint'
import { useStores } from 'hooks/useStores'
import { observer } from 'mobx-react'

// Lazy load ReactPlayer for better performance
const ReactPlayer = lazy(() => import('react-player'))
import Iframe from 'components/Iframe'
import { convertToEmbedLink, getOfficeAppLiveUrl } from 'components/MediaTab/utils'
import SvgIcon from 'components/SvgIcon'
import { S3FileTypeEnum } from 'constants/aws'
import { MediaType } from 'constants/media'
import { EBreakPoint } from 'constants/theme'
import { IMedia } from 'interfaces/media'
import { getS3FileUrlByMediaType } from 'utils/common'
import { getS3FileUrl } from 'utils/images'

interface IMediaViewProps {
  mediaList: IMedia[]
  mediaIndex: number
  setSelectedMedia: (media: IMedia) => void
}

const MediaView = (props: IMediaViewProps) => {
  const { mediaList, mediaIndex = 0, setSelectedMedia } = props
  const { organizationStore } = useStores()
  const [currentIndex, setCurrentIndex] = useState<number>(mediaIndex)
  const currentMedia: IMedia = mediaList[currentIndex]
  const isMobile: boolean = useBreakPoint(EBreakPoint.BASE, EBreakPoint.MD)

  function handleClickPrev(): void {
    if (currentIndex > 0) {
      setSelectedMedia(mediaList[currentIndex - 1])
      setCurrentIndex(currentIndex - 1)
    }
  }
  function handleClickNext(): void {
    if (currentIndex < mediaList?.length - 1) {
      setSelectedMedia(mediaList[currentIndex + 1])
      setCurrentIndex(currentIndex + 1)
    }
  }

  useEffect(() => {
    setCurrentIndex(mediaIndex)
  }, [mediaIndex])

  return (
    <>
      {currentMedia && (
        <Center width="full" height="full" borderRadius={12} border="1px solid #E2E8F0" position="relative">
          {currentMedia?.mediaType !== MediaType.EMBED_LINK && (
            <Button
              gap={2}
              variant="outline"
              color="gray.700"
              background="white"
              fontWeight={500}
              lineHeight={6}
              position="absolute"
              top={2}
              right={2}
              zIndex={2}
            >
              <SvgIcon iconName="download" size={16} />
              <chakra.a
                color="gray.700"
                target="_blank"
                href={getS3FileUrlByMediaType(currentMedia, organizationStore.organization?.id ?? 0)}
                rel="noreferrer"
                _hover={{ color: 'gray.700' }}
                onClick={event => event.stopPropagation()}
              >
                Download
              </chakra.a>
            </Button>
          )}
          {currentMedia?.mediaType === MediaType.IMAGE && (
            <Image
              width="full"
              height={{ base: '212px', md: '612px' }}
              borderRadius={12}
              objectFit="contain"
              alt={currentMedia.name}
              src={getS3FileUrl(
                S3FileTypeEnum.IMAGE,
                organizationStore.organization?.id ?? 0,
                currentMedia?.image ?? currentMedia?.originalImage
              )}
            />
          )}
          {currentMedia?.mediaType === MediaType.VIDEO && (
            <Suspense fallback={<Spinner />}>
              <ReactPlayer
                width="full"
                height={isMobile ? '212px' : '612px'}
                url={getS3FileUrl(
                  S3FileTypeEnum.VIDEO,
                  organizationStore.organization?.id ?? 0,
                  currentMedia?.video || currentMedia?.url || ''
                )}
                controls
              />
            </Suspense>
          )}
          {currentMedia?.mediaType === MediaType.FILE && (
            <Iframe
              width="full"
              height={{ base: '212px', md: '612px' }}
              src={getOfficeAppLiveUrl(
                getS3FileUrl(S3FileTypeEnum.OTHER, organizationStore.organization?.id ?? 0, currentMedia?.document)
              )}
            />
          )}
          {currentMedia?.mediaType === MediaType.EMBED_LINK && (
            <Iframe
              width="full"
              height={{ base: '212px', md: '612px' }}
              src={convertToEmbedLink(currentMedia?.url ?? '')}
            />
          )}
          <HStack width="full" justify="space-between" paddingX={3} position="absolute" top="50%">
            <Button border={0} boxSize={10} background="gray.100" onClick={handleClickNext}>
              <ArrowDownIcon boxSize={5} />
            </Button>
            <Button border={0} boxSize={10} background="gray.100" onClick={handleClickPrev}>
              <ArrowUpIcon boxSize={5} />
            </Button>
          </HStack>
        </Center>
      )}
    </>
  )
}
export default observer(MediaView)
